import { Close } from "@mui/icons-material";
import {
  Alert,
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useState } from "react";
import { useAddCreditNoteToInvoiceMutation } from "src/api/services/invoicesApi";
import { theme } from "src/assets/theme";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { desktopSx } from "src/utils/styles/modalStyles";

interface ModalProps {
  open: boolean;
  onClose: (i: string) => void;
  invoice: string | null;
}

const CreditNoteModal = ({ open, onClose, invoice }: ModalProps) => {
  const [addCreditNote] = useAddCreditNoteToInvoiceMutation();
  const [amount, setAmount] = useState<number | null>(null);
  const [memo, setMemo] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>("");
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (invoice == null) return null;

      try {
        setLoading(true);
        setError("");
        if (amount == null || memo == null) {
          setError("Please enter the amount and memo for the credit note");
          return;
        }

        const stripeAmount = Math.round(amount * 100);

        await addCreditNote({
          invoice,
          memo,
          amount: stripeAmount,
        });
        setShowAlert(true);
        setDisabled(true);
      } catch (err) {
        console.error(`Unable to add credit note to invoice: ${invoice}`, err);
      } finally {
        setLoading(false);
      }
    },
    [addCreditNote, amount, invoice, memo],
  );

  const handleOnClose = useCallback(() => {
    onClose("");
    setError(null);
    setAmount(null);
    setMemo(null);
    setShowAlert(false);
    setDisabled(false);
  }, [onClose]);

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Add Credit Note
          </Typography>
          <IconButton onClick={handleOnClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack
          component="form"
          noValidate
          autoComplete="off"
          gap={2}
          onSubmit={handleSubmit}
        >
          <FormControl>
            <TextField
              required
              label="Amount ($)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </FormControl>
          <FormControl>
            <TextField
              required
              label="Memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </FormControl>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error.toString()}
            </Typography>
          ) : null}
          {showAlert ? (
            <Alert
              variant="filled"
              severity="success"
              sx={{ bgcolor: theme.palette.primary.main }}
            >
              {`A $${amount} credit note has been added to invoice: ${invoice}`}
            </Alert>
          ) : null}
          <LoadingButton type="submit" loading={loading} disabled={disabled}>
            Add credit note
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreditNoteModal;
